import { shouldUseNopBalance, shouldUseNopPositions } from '@/helpers/environmentHelper';
import { logToServer } from '@/services/LogService';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { setBalanceStatus, setNopBalance, setNopBalanceError } from '@/state/reducers/balanceSlice';
import { selectCurrencyConfigs } from '@/state/reducers/ohoSlice';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { WebSocketHook } from 'react-use-websocket/dist/lib/types';
import { toastLogout } from '../hooks/useToast';
import { Logger } from '../logger';

interface Balance {
    amount: number;
    amountInBaseCurrency: number;
    currencyName: string;
    tradeIds: number[];
    valueDate: string;
}

export interface Balances {
    balances: Balance[];
    baseCurrency: string;
    isHeartBeat: boolean;
}

export interface BalancesContext extends Partial<WebSocketHook> {}

export interface BalancesProviderProps {
    children: React.ReactNode;
}

export const errorMessages = [
    'Session token is missing',
    'Session token is invalid',
    'Access token is no longer valid'
];

export const warnMessages = ['Celer Bridge is not available', 'Temporary internal error occurred'];

const nopBalancesURL = window.config?.balances?.websocket;

const BalancesContext = createContext({} as BalancesContext);
export const useBalances = () => useContext(BalancesContext);
export const BalancesProvider = (props: BalancesProviderProps) => {
    const dispatch = useAppDispatch();
    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);
    const currencyConfigs = useAppSelector(selectCurrencyConfigs);

    const didUnmount = useRef(false);

    const isDeliverableAccount = useMemo(() => {
        if (currentAccount && credentials) {
            const accountProperties = credentials.accounts.find(
                (account) => account.code === currentAccount
            )?.properties;
            return accountProperties?.find((property) => property.key === 'TYPE')?.value === 'NOP';
        }
    }, [currentAccount, credentials]);

    const wsURL = useMemo(() => {
        if (credentials && currentAccount && (isDeliverableAccount || shouldUseNopPositions) && shouldUseNopBalance) {
            return `${nopBalancesURL}/${credentials.username}/${currentAccount}?sessionToken=${credentials.authToken}`;
        } else return null;
    }, [credentials, currentAccount]);

    const options = useMemo(
        () => ({
            reconnectAttempts: 5,
            retryOnError: true,
            reconnectInterval: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
            shouldReconnect: (event) => event.code !== 1000,
            onError: (e) => {
                dispatch(setNopBalanceError('WebSocket connection error'));
                if (credentials?.username && currentAccount) {
                    logToServer(
                        'warn',
                        `User [${
                            credentials.username
                        } | ${currentAccount}] encountered error on BalancesProvider WebSocket: ${JSON.stringify(e)}.`
                    );
                }
                Logger({
                    title: `BalancesProvider WebSocket Error.`,
                    callback: () => {
                        console.log(e);
                    }
                });
            }
        }),
        []
    );

    const websocket = useWebSocket<Balances>(wsURL, options);

    const { readyState, lastJsonMessage, lastMessage, getWebSocket } = websocket;

    useEffect(() => {
        dispatch(setBalanceStatus(ReadyState[readyState] as keyof typeof ReadyState));
    }, [readyState]);

    useEffect(() => {
        if (lastJsonMessage?.balances) {
            dispatch(
                setNopBalance({
                    lastJsonMessage,
                    account: currentAccount,
                    currencyConfigs,
                    getAssetName: (ccy: string) => {
                        const config = currencyConfigs.find(
                            (currencyConfig) => currencyConfig.celer === ccy || currencyConfig.show === ccy
                        );
                        return config?.assetName || ccy;
                    }
                })
            );
        }
    }, [lastJsonMessage]);

    useEffect(() => {
        if (errorMessages.includes(lastMessage?.data || '') && credentials) {
            dispatch(setNopBalanceError(lastMessage?.data || ''));
            Logger({
                title: `Inbound: NOP Balances Error - Message: ${lastMessage?.data || ''}.`,
                callback: () => {}
            });
            logToServer(
                'warn',
                `User [${credentials.username}] has been logged out due to NOP Balances error - Message: ${
                    lastMessage?.data || ''
                }`
            );
            toastLogout(credentials);
        } else if (warnMessages.includes(lastMessage?.data || '') && credentials) {
            dispatch(setNopBalanceError(lastMessage?.data || ''));
            Logger({
                title: `Inbound: NOP Balances Warning - Message: ${lastMessage?.data || ''}.`,
                callback: () => {}
            });
            logToServer(
                'warn',
                `User [${credentials.username}] is missing Cash Balances due to NOP Balances error - Message: ${
                    lastMessage?.data || ''
                }`
            );
        }
    }, [lastMessage, credentials]);

    const value = useMemo<BalancesContext>(() => {
        return { readyState, lastJsonMessage, getWebSocket };
    }, [readyState, lastJsonMessage, getWebSocket]);

    useEffect(() => {
        return () => {
            didUnmount.current = true;
        };
    }, []);

    return <BalancesContext.Provider value={value}>{props.children}</BalancesContext.Provider>;
};
